import { PaginatedDataDTO } from '@bottega52/commons-pagination';
import { Button, Checkbox, FormControlLabel, TextField, WithStyles } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import * as CreditsActions from '../../redux/credits/credits.actions';
import * as UserActions from '../../redux/user/user.actions';
import * as DomainsActions from '../../redux/domains/domains.actions';
import { IDomainOptionDTO } from '../../repository/jago/model/input/IDomainOptionDTO';
import { IUserInDTO } from '../../repository/jago/model/input/IUserInDTO';
import { IUserOptionDTO } from '../../repository/jago/model/input/IUserOptionDTO';
import { IUserRequestParamsDTO } from '../../repository/jago/model/output/RequestParamsDTOs';
import { getDomainNameFromHostname, getDomainsOptions, getUsersOptions } from '../../repository/utils';
import ReduxLanguage from '../ReduxLanguage';
import { IDemoPlantInDTO } from '../../repository/jago/model/input/IDemoPlantInDTO';
import { IDomainConfigParsedDTO } from '../../repository/jago/model/input/IDomainConfigInDTO';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: 20,
    gap: 20
  },
  formField: {
    marginBottom: 10
  }
});

export interface IUserDemoPlantAssociationFormProps extends WithStyles<typeof styles> {
  onSubmit: (values: any) => void;
  demoPlantAssociation?: IDemoPlantInDTO | undefined;
  demoPlantConfig?: IDomainConfigParsedDTO | undefined;
}

export interface IUserDemoPlantAssociationFormState {
  name: string;
  note: string;
  domainSelected: IDomainOptionDTO | undefined;
  userSelected: IUserOptionDTO | undefined;
  searchDomainText: string;
  searchUserText: string;
  domainsOptions: IDomainOptionDTO[];
  usersOptions: IUserOptionDTO[];
  otpLoginOnly: boolean;
  domainConfig: IDomainConfigParsedDTO;
}

class UserDemoPlantAssociationForm extends React.Component<IUserDemoPlantAssociationFormProps, IUserDemoPlantAssociationFormState> {
  
  constructor(props: IUserDemoPlantAssociationFormProps) {
    super(props);
    const demoPlantAssociation = props.demoPlantAssociation;
    const domainConfig = props.demoPlantConfig ? props.demoPlantConfig : {} as IDomainConfigParsedDTO;
    this.state = {
      name: demoPlantAssociation ? demoPlantAssociation.name : '',
      note: demoPlantAssociation ? demoPlantAssociation.note : '',
      domainSelected: demoPlantAssociation ? { value: demoPlantAssociation.plant, label: getDomainNameFromHostname(demoPlantAssociation.plant.hostname) } : undefined,
      userSelected: demoPlantAssociation ? { value: demoPlantAssociation.user, label: demoPlantAssociation.user.firstname + ' ' + demoPlantAssociation.user.lastname + ' (' + demoPlantAssociation.user.email + ')' } : undefined,
      searchDomainText: '',
      searchUserText: '',
      domainsOptions: [],
      usersOptions: [],
      otpLoginOnly: domainConfig ? domainConfig.otpLoginOnly : false,
      domainConfig: domainConfig,
    }
  }

  async onSelectDomain(domain: IDomainOptionDTO) {
    if (domain) {
      this.setState({ domainSelected: domain });
      const { dispatch } = this.props as any;
      const domainConfig = await dispatch<any>(DomainsActions.fetchSofiaDomainConfigurationByHostname(domain.value.hostname));
      if (domainConfig) {
        this.setState({ otpLoginOnly: domainConfig.otpLoginOnly, domainConfig: domainConfig });
      } else {
        this.setState({ otpLoginOnly: false, domainConfig: {} as IDomainConfigParsedDTO });
      }
    } else {
      this.setState({ domainSelected: undefined, otpLoginOnly: undefined });
    }
  }

  onSelectUser(user: IUserOptionDTO) {
    if (user) {
      this.setState({ userSelected: user });
    } else {
      this.setState({ userSelected: undefined });
    }
  }

  onSetOtpLoginOnly(value: boolean) {
    const { domainConfig } = this.state;
    domainConfig.otpLoginOnly = value;
    console.log(domainConfig);
    this.setState({ domainConfig: domainConfig, otpLoginOnly: value });
  }

  async onFetchDomains(value: string) {
    const { dispatch } = this.props as any;
    const params = { page: 0, pageSize: 1000, hostname: value, hostName: value, walletId: undefined, isCloudCredits: false };
    const domains = await dispatch<any>(CreditsActions.fetchDomainsSearch({...params, modality: 'CREDITS'}));
    const domainsOptions = getDomainsOptions(domains);
    this.setState({ domainsOptions: domainsOptions as IDomainOptionDTO[] });
  }

  async onFetchUsers(value: string) {
    const { dispatch } = this.props as any;
    const params: IUserRequestParamsDTO = { search: value, page: 0, pageSize: 1000 };
    const filteredParams: IUserRequestParamsDTO = _.pickBy(params, param => param !== '') as IUserRequestParamsDTO;
    const users: PaginatedDataDTO<IUserInDTO>= await dispatch<any>(UserActions.fetchUsers(filteredParams));
    const usersOptions = getUsersOptions(users.content);
    this.setState({ usersOptions: usersOptions as IUserOptionDTO[] });
  }

  canSubmitForm() {
    const { name, note, domainSelected, userSelected, otpLoginOnly } = this.state;
    return name && note && domainSelected && userSelected && otpLoginOnly !== undefined;
  }

  onSubmitForm() {
    const { onSubmit } = this.props as any;
    const { name, note, domainSelected, userSelected, otpLoginOnly, domainConfig } = this.state;
    onSubmit({
      name,
      note,
      plantId: domainSelected.value.id,
      userId: userSelected.value.id,
      otpLoginOnly,
      domainConfig,
      domainSelected,
    });
  }

  public render() {
    const { classes } = this.props as any;
    const { domainsOptions, usersOptions } = this.state;
    const { name, note, domainSelected, userSelected, searchDomainText, searchUserText, otpLoginOnly } = this.state;
    
    const debouncedDomainSearch = _.debounce((text) => this.onFetchDomains(text), 500);
    const debouncedUserSearch = _.debounce((text) => this.onFetchUsers(text), 500);
    const isEdit = this.props.demoPlantAssociation?.id;

    return (
      <div className={classes.container}>
        <TextField
          id="name-field"
          label={<ReduxLanguage languageKey="forms.name" />}
          variant="outlined"
          fullWidth
          className={classes.formField}
          value={name}
          onChange={e => this.setState({ name: e.target.value })}
        />

        <Autocomplete
          options={domainsOptions}
          isClearable={!isEdit}
          disabled={isEdit}
          autoComplete
          style={{ marginBottom: 10 }}
          className={classes.formField}
          inputValue={domainSelected ? domainSelected.label : searchDomainText}
          value={domainSelected}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => 
            <TextField 
              {...params} 
              variant="outlined" 
              label={<ReduxLanguage languageKey="forms.domainName" />}
            />
          }
          onInputChange={(event, newInputValue) => { 
            this.setState({ searchDomainText: newInputValue }); 
            debouncedDomainSearch(newInputValue); 
          }}
          onChange={(e, newValue) => this.onSelectDomain(newValue as IDomainOptionDTO)}
        />
        {domainSelected && (
          <div style={{ marginTop: -20, marginLeft: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={otpLoginOnly} onChange={(e, checked) => this.onSetOtpLoginOnly(checked)} name="otpLoginOnly" />}
              label={<ReduxLanguage languageKey={'forms.otpLoginOnly'} />}
            />
          </div>
        )}
        <Autocomplete
          options={usersOptions}
          isClearable={!isEdit}
          disabled={isEdit}
          autoComplete
          className={classes.formField}
          inputValue={userSelected ? userSelected.label : searchUserText}
          value={userSelected}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => 
            <TextField 
              {...params} 
              variant="outlined" 
              label={<ReduxLanguage languageKey="forms.user" />}
            />
          }
          onInputChange={(event, newInputValue) => { 
            this.setState({ searchUserText: newInputValue }); 
            debouncedUserSearch(newInputValue); 
          }}
          onChange={(e, newValue) => this.onSelectUser(newValue as IUserOptionDTO)}
        />

        <TextField
          id="note-field"
          label={<ReduxLanguage languageKey="demoSystem.notes" />}
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          className={classes.formField}
          value={note}
          onChange={e => this.setState({ note: e.target.value })}
        />
        <Button
          variant="contained"
          disabled={!this.canSubmitForm()}
          style={{ width: '100%', opacity: this.canSubmitForm() ? 1 : 0.5, backgroundColor: '#5AC0B1', color: 'white', borderRadius: 0, height: 45, fontWeight: 'bold'  }}
          onClick={() => this.onSubmitForm()}
        >
          <ReduxLanguage languageKey="forms.save" />
        </Button>
      </div>
    );
  }
}

const connector = connect();
export default connector(withStyles(styles)(UserDemoPlantAssociationForm));
